// components/ProgramsSection.tsx
import SportsBasketballRoundedIcon from '@mui/icons-material/SportsBasketballRounded';
import PoolRoundedIcon from '@mui/icons-material/PoolRounded';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import DirectionsRunOutlinedIcon from '@mui/icons-material/DirectionsRunOutlined';
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import {
  Avatar,
  Box,
  CardContent,
  Container,
  Grid,
  Typography
} from "@mui/material";
import { motion } from "framer-motion";
import React from "react";
import { Programs_IMG } from '../../Constants/ImageConstants';
import { COLORS } from "../../Constants/styles";
import CustomTextButton from "../Common/CustomButton";

interface SportsProgram {
  title: string;
  category: string;
  description: string;
  buttonLabel: string;
  icon: React.ElementType;
  image: string;
  slug: string;
}

interface TennisProgram {
  title: string;
  description: string;
  category: string;
  link: string;
  image: string;
  icon: React.ElementType;
  slug: string;
}

export const sportsPrograms: SportsProgram[] = [
  {
    slug: 'in-school-sports',
    category: "In-School Sports & Physical Education Programs",
    title: "Curriculum-aligned Sports & PE from KG to Grade 12",
    description:
      "Delivering multimedia-based (image, text, video) Sports & PE programs during school hours to build physical literacy, teamwork, and lifelong love for movement. ",
    buttonLabel: "Learn More",
    icon: SportsBasketballRoundedIcon,
    image: Programs_IMG.kidsnadultsImage,
  },
  {
    slug: 'coach-education',
    category: "Grassroots Sports Coach Education",
    title: "Strengthening the sports ecosystem from the ground up ",
    description:
      "Empowering coaches, educators, and communities through age-appropriate training, inclusive coaching practices, and sports science fundamentals.",
    buttonLabel: "Learn More",
    icon: PoolRoundedIcon,
    image: Programs_IMG.kidsnadults_2Image,
  },
];

export const tennisPrograms: TennisProgram[] = [
  {
    slug: 'recruitment-hr',
    category: "Sports Professional Recruitment & HR Services",
    title: "Connecting talent with opportunity in the sports industry",
    description:
      "We recruit, train, and deploy qualified coaches, PE teachers, trainers, and sports staff across schools, academies, and organizations.",
    link: "/fitness",
    image: Programs_IMG.kidsnadults_3Image,
    icon: GroupsOutlinedIcon,
  },
  {
    slug: 'events-management',
    category: "Sports Events & Tournament Management",
    title: "Seamless planning and execution of sports events",
    description:
      "From small school events to large-scale festivals, we handle everything planning, branding, officiating, and execution.",
    link: "/camps",
    image: Programs_IMG.kidsnadults_4Image,
    icon: EmojiEventsOutlinedIcon,
  },
  {
    slug: '',
    category: "After-School Sports Programs",
    title: "Pathway for young talent to train and excel",
    description:
      "Talent identification and high-performance training programs for aspiring athletes looking to progress toward elite competition levels.",
    link: "/rent",
    image: Programs_IMG.kidsnadults_5Image,
    icon: SchoolOutlinedIcon,
  },
  {
    slug: 'sports-science',
    category: "Sports Science Consultation Services",
    title: "Applying science to enhance performance and safety",
    description:
      "Delivering expert consultation on injury prevention, recovery, biomechanics, and physical assessment to optimize athlete development.",
    link: "/daycare",
    image: Programs_IMG.kidsnadults_6Image,
    icon: ScienceOutlinedIcon,
  },
  {
    slug: 'infrastructure',
    category: "Sports Infrastructure Consultation Services",
    title: "Building safe, functional, and child-friendly sports spaces",
    description:
      "From concept to execution, we help schools and communities design and implement sports facilities that inspire play and safety.",
    link: "/daycare",
    image: Programs_IMG.kidsnadults_6Image,
    icon: ApartmentOutlinedIcon,
  },
  {
    slug: 'youth-fitness',
    category: "Youth Fitness & Conditioning Programs",
    title: "Age-appropriate fitness for growing bodies and minds",
    description:
      "Customized programs focused on strength, flexibility, agility, and endurance to support healthy development and sports readiness.",
    link: "/daycare",
    image: Programs_IMG.kidsnadults_6Image,
    icon: DirectionsRunOutlinedIcon,
  },
  {
    slug: 'sportswear-retail',
    category: "Sportswear & Equipment Retail",
    title: "Reliable gear for schools, academies, and teams",
    description:
      "We supply high-quality sportswear, team kits, training equipment, and accessories to support all levels of play and training.",
    link: "/daycare",
    image: Programs_IMG.kidsnadults_6Image,
    icon: StoreOutlinedIcon,
  },
  {
    slug: 'branding-sponsorship',
    category: "Sports Branding & Sponsorship Solutions",
    title: "Amplify your identity and unlock growth",
    description:
      "We build strong sports brands and connect them with sponsors to enhance visibility, engagement, and long-term success in grassroots sport.",
    link: "/daycare",
    image: Programs_IMG.kidsnadults_6Image,
    icon: CampaignOutlinedIcon,
  },
];

const ProgramsSection = () => {
  return (
    <>
      <Box sx={{ bgcolor: COLORS.accentOffWhite, py: 1 }}>
        <Container maxWidth="xl" sx={{ bgcolor: COLORS.accentOffWhite, borderRadius: 2, px: { xs: 0, md: 4 }, py: 2 }}>
          <Box textAlign="center" mb={5}>
            <Typography variant="subtitle1" sx={{ backgroundColor: COLORS.accentLime, color: COLORS.pureBlack, px: 2, mb: 2, py: 0.5, borderRadius: '4px', display: 'inline-block' }}>What We Bring to You</Typography>
            <Typography variant="h3" sx={{ mt: 2, color: COLORS.pureBlack }}>Empowering  Every Child, Coach & School</Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center" sx={{ px: { xs: 2, sm: 2, md: 2, lg: 2 } }}>
            {sportsPrograms.map((program, idx) => {
              const IconComponent = program.icon;
              return (
                <Grid item xs={12} md={6} lg={6} key={idx}>
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.5, delay: idx * 0.2 }}
                  >
                    <Box sx={{ borderRadius: 4, overflow: "hidden", display: "flex", flexDirection: { xs: "column", md: "row" }, bgcolor: COLORS.white, boxShadow: 3, height: "100%" }}>
                      <Box component="img" src={program.image} alt={program.title} sx={{ width: { xs: "100%", md: "50%" }, objectFit: "cover" }} />
                      <CardContent sx={{ p: 3, position: "relative", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", alignItems: "center", flexDirection: { xs: "column", sm: "row" }, gap: 1, position: "absolute", top: { xs: -25, sm: 6, md: 3 }, left: { xs: 8, sm: -10, md: -30 }, bgcolor: COLORS.white, px: 2, py: 0.5, borderRadius: "50%", height: "48px" }}>
                          <Avatar sx={{ bgcolor: COLORS.greyBG, width: 34, height: 34 }}>
                            <IconComponent sx={{ fontSize: 32, stroke: '#000', strokeWidth: 0.5, fill: 'none', color: COLORS.accentBlackTransparent }} />
                          </Avatar>
                        </Box>
                        <Typography variant="h5" fontWeight={600} sx={{ mt: { xs: 2, sm: 2, md: 2.5 }, position: "absolute", top: { xs: 8, sm: 6, md: 1 }, left: { xs: 25, sm: 10, md: 23 } }}>{program.category}</Typography>
                        <Box>
                          <Typography variant="h4" sx={{ mt: 10, fontWeight: 700 }}>{program.title}</Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>{program.description}</Typography>
                        </Box>
                        <CustomTextButton label={program.buttonLabel} href={`/ourservices#${program.slug}`} />
                      </CardContent>
                    </Box>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ pt: 1, pb: { xs: 4, sm: 8, md: 8 }, backgroundColor: COLORS.greyBG }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 6 } }}>
          <Grid container spacing={4}>
            {tennisPrograms.map((program, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: idx * 0.2 }}
                  style={{ height: "100%" }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: COLORS.white, borderRadius: 4, overflow: "hidden", boxShadow: 3, position: "relative" }}>
                    <Box component="img" src={program.image} alt={program.title} sx={{ width: "100%", height: 200, objectFit: "cover" }} />
                    <Box sx={{ p: 3, flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                      <Box sx={{ position: "absolute", top: 175, left: 16, zIndex: 2, backgroundColor: COLORS.white, borderRadius: "50%", width: 58, height: 58, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {program.icon && (
                          <program.icon sx={{ fontSize: 32, stroke: '#000', strokeWidth: 0.5, fill: 'none', color: COLORS.accentBlackTransparent }} />
                        )}
                      </Box>
                      <Typography variant="h5" fontWeight={600} sx={{ mb: 2, mt: 4 }}>{program.category}</Typography>
                      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>{program.title}</Typography>
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>{program.description}</Typography>
                      <CustomTextButton label="Learn More" href={program.slug ? `/ourservices#${program.slug}` : '/ourservices'} />
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default ProgramsSection;